const normalizePolicySet = (accessMap) => {
  const policy = Array.isArray(accessMap?.publicAccessPolicy)
    ? accessMap.publicAccessPolicy
    : [];
  return Array.from(new Set(policy.map((item) => String(item || "").trim()).filter(Boolean))).sort();
};

const normalizeRoleAccessList = (accessMap) => {
  const defaults = {
    OWNER: "RESTRICTED",
    CONTRIBUTOR: "RESTRICTED",
    VIEWER: "RESTRICTED",
  };

  if (accessMap?.roleAccessList && typeof accessMap.roleAccessList === "object") {
    return {
      OWNER: String(accessMap.roleAccessList.OWNER || defaults.OWNER),
      CONTRIBUTOR: String(accessMap.roleAccessList.CONTRIBUTOR || defaults.CONTRIBUTOR),
      VIEWER: String(accessMap.roleAccessList.VIEWER || defaults.VIEWER),
    };
  }

  const policy = normalizePolicySet(accessMap);
  const policySet = new Set(policy);
  return {
    OWNER: defaults.OWNER,
    CONTRIBUTOR: policySet.has("CONTRIBUTOR") ? "PUBLIC" : defaults.CONTRIBUTOR,
    VIEWER: policySet.has("VIEWER") || policySet.has("CONTRIBUTOR") ? "PUBLIC" : defaults.VIEWER,
  };
};

const normalizeUserIdentity = (userRole) => {
  if (!userRole || typeof userRole !== "object") return null;
  const username = String(userRole.username || "").trim();
  const userId = String(userRole.userId || userRole.principalKey || "").trim();
  if (!username && !userId) return null;
  return {
    username: username || null,
    userId: userId || null,
  };
};

const buildUserAliases = (roles) => {
  const aliases = {
    idToUsername: new Map(),
    usernameToId: new Map(),
  };

  roles.forEach((role) => {
    const identity = normalizeUserIdentity(role);
    if (!identity) return;
    if (identity.userId && identity.username) {
      aliases.idToUsername.set(identity.userId, identity.username);
      aliases.usernameToId.set(identity.username, identity.userId);
    }
  });

  return aliases;
};

const resolveStableRowKey = (identity, aliases) => {
  if (!identity) return null;
  if (identity.username) return identity.username;
  if (identity.userId && aliases?.idToUsername.has(identity.userId)) {
    return aliases.idToUsername.get(identity.userId);
  }
  return identity.userId || null;
};

const normalizeUserRole = (userRole, aliases) => {
  const identity = normalizeUserIdentity(userRole);
  const rowKey = resolveStableRowKey(identity, aliases);
  if (!rowKey) return null;
  const keys = Array.from(
    new Set(
      [rowKey, identity?.username, identity?.userId, userRole?.principalKey]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
  return {
    rowKey,
    role: String(userRole?.roletype || "").trim(),
    keys,
  };
};

const getUserRoleMap = (accessMap, aliases) => {
  const roles = Array.isArray(accessMap?.userRoles) ? accessMap.userRoles : [];
  const out = new Map();
  roles.forEach((role) => {
    const normalized = normalizeUserRole(role, aliases);
    if (!normalized) return;
    out.set(normalized.rowKey, normalized);
  });
  return out;
};

const extractCustomFieldName = (field) => {
  const fromName = String(field?.name || "").trim();
  if (fromName) return fromName;
  const fromKey = String(field?.key || "").trim();
  if (fromKey) return fromKey;
  const fromId = String(field?.id || "").trim();
  return fromId || null;
};

const extractCustomFieldValue = (field) => {
  if (!field || typeof field !== "object") return null;
  if ("value" in field) return field.value;
  return null;
};

const getCustomDataMap = (customData) => {
  const list = Array.isArray(customData) ? customData : [];
  const out = new Map();
  list.forEach((field) => {
    const name = extractCustomFieldName(field);
    if (!name) return;
    out.set(name, extractCustomFieldValue(field));
  });
  return out;
};

const areValuesEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const normalizeChangePathPart = (value) => String(value || "").trim().toLowerCase();

const extractUserKeyCandidates = (value) => {
  const candidates = new Set();
  if (value == null) return candidates;

  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return candidates;
    candidates.add(raw);

    // Backend role display values can look like "username ☰ OWNER".
    const displayUsername = raw.split("☰")[0]?.trim();
    if (displayUsername) {
      candidates.add(displayUsername);
    }
    return candidates;
  }

  if (typeof value === "object") {
    [value.username, value.userId, value.principalKey]
      .map((entry) => String(entry || "").trim())
      .filter(Boolean)
      .forEach((entry) => candidates.add(entry));
    return candidates;
  }

  const fallback = String(value).trim();
  if (fallback) {
    candidates.add(fallback);
  }
  return candidates;
};

const buildDetailsFromRawChanges = (rawChanges) => {
  if (!Array.isArray(rawChanges) || rawChanges.length === 0) return null;

  const changedUserKeys = new Set();
  const changedCustomFieldNames = new Set();
  let publicAccessChanged = false;

  rawChanges.forEach((entry) => {
    const fieldName = normalizeChangePathPart(entry?.fieldName);
    const subFieldName = normalizeChangePathPart(entry?.subFieldname);
    const action = normalizeChangePathPart(entry?.action);

    if (fieldName.includes("accessmap")) {
      const accessMapPath = `${fieldName}.${subFieldName}`;
      if (
        accessMapPath.includes("publicaccesspolicy") ||
        accessMapPath.includes("publicaccessmode") ||
        accessMapPath.includes("roleaccesslist")
      ) {
        publicAccessChanged = true;
      }

      if (
        accessMapPath.includes("userroles") ||
        subFieldName === "userid" ||
        subFieldName === "username" ||
        subFieldName === "roletype"
      ) {
        const rawCandidates = [
          entry?.oldDisplayValue,
          entry?.displayValue,
          entry?.oldValue,
          entry?.value,
        ];

        rawCandidates.forEach((value) => {
          extractUserKeyCandidates(value).forEach((candidate) => changedUserKeys.add(candidate));
        });
      }
    }

    if (fieldName.includes("customdata")) {
      const candidateName = String(entry?.subFieldname || "").trim();
      if (candidateName) {
        changedCustomFieldNames.add(candidateName);
      } else if (action === "added" || action === "removed" || action === "updated") {
        // If backend omitted subFieldname, mark customData as changed without row specificity.
        changedCustomFieldNames.add("*");
      }
    }
  });

  return {
    accessMap: {
      publicAccessChanged,
      changedUserKeys: Array.from(changedUserKeys),
    },
    customData: {
      changedFieldNames: Array.from(changedCustomFieldNames),
    },
  };
};

export const buildRealtimeFieldDetails = (previousRecord, nextRecord, rawChanges = []) => {
  const detailsFromRawChanges = buildDetailsFromRawChanges(rawChanges);
  if (detailsFromRawChanges) {
    return detailsFromRawChanges;
  }

  const prevAccessMap = previousRecord?.accessMap || null;
  const nextAccessMap = nextRecord?.accessMap || null;
  const prevPolicy = normalizePolicySet(prevAccessMap);
  const nextPolicy = normalizePolicySet(nextAccessMap);
  const prevRoleAccessList = normalizeRoleAccessList(prevAccessMap);
  const nextRoleAccessList = normalizeRoleAccessList(nextAccessMap);

  const prevRoles = Array.isArray(prevAccessMap?.userRoles) ? prevAccessMap.userRoles : [];
  const nextRoles = Array.isArray(nextAccessMap?.userRoles) ? nextAccessMap.userRoles : [];
  const aliases = buildUserAliases([...prevRoles, ...nextRoles]);
  const prevRoleMap = getUserRoleMap(prevAccessMap, aliases);
  const nextRoleMap = getUserRoleMap(nextAccessMap, aliases);
  const allRoleKeys = new Set([...prevRoleMap.keys(), ...nextRoleMap.keys()]);
  const changedUserKeys = Array.from(
    Array.from(allRoleKeys).reduce((keys, key) => {
      const previous = prevRoleMap.get(key);
      const next = nextRoleMap.get(key);
      if (previous?.role !== next?.role) {
        (previous?.keys || []).forEach((candidate) => keys.add(candidate));
        (next?.keys || []).forEach((candidate) => keys.add(candidate));
      }
      return keys;
    }, new Set())
  );

  const prevCustomMap = getCustomDataMap(previousRecord?.customData);
  const nextCustomMap = getCustomDataMap(nextRecord?.customData);
  const allCustomNames = new Set([...prevCustomMap.keys(), ...nextCustomMap.keys()]);
  const changedCustomFieldNames = Array.from(allCustomNames).filter(
    (name) => !areValuesEqual(prevCustomMap.get(name), nextCustomMap.get(name))
  );

  return {
    accessMap: {
      publicAccessChanged:
        !areValuesEqual(prevPolicy, nextPolicy) ||
        !areValuesEqual(prevRoleAccessList, nextRoleAccessList),
      changedUserKeys,
    },
    customData: {
      changedFieldNames: changedCustomFieldNames,
    },
  };
};

